import * as THREE from "three"

/**
 * Pinta un único carril (ribbon) a lo largo de `verts` cubriendo `lengthToCover`
 * metros, con `laneWidth` de espesor y desplazado perpendicularmente al eje del
 * segmento por `offsetY`. Añade quads (PlaneGeometry) al `group` dado.
 *
 * Función de render REUTILIZABLE (la usan el overlay de avance y el de resaltado).
 * Itera los pares (vᵢ, vᵢ₊₁) consumiendo metros; el offset usa el vector normal al
 * segmento → funciona en polilíneas con giros. `startOffset` salta metros desde el
 * inicio (pintado concatenado). El color va en `material` (compartido por carril).
 *
 * @param {THREE.Group} group
 * @param {Array<{x:number,y:number}>} verts
 * @param {{x:number,y:number}} origin
 * @param {number} lengthToCover
 * @param {number} laneWidth
 * @param {number} offsetY
 * @param {THREE.Material} material
 * @param {number} statusOrderIndex  - escalona Z/renderOrder entre carriles
 * @param {?object} statusMeta        - {status,statusLabel,statusColor,meters} → userData del mesh
 * @param {number} [startOffset=0]
 */
export function paintLane(group, verts, origin, lengthToCover, laneWidth, offsetY, material, statusOrderIndex, statusMeta, startOffset = 0) {
  let skip = Math.max(0, startOffset)
  let remaining = lengthToCover
  for (let i = 0; i < verts.length - 1 && remaining > 1e-9; i++) {
    const a = verts[i], b = verts[i + 1]
    if (!Number.isFinite(a?.x) || !Number.isFinite(a?.y) ||
        !Number.isFinite(b?.x) || !Number.isFinite(b?.y)) continue

    const ax = a.x - origin.x, ay = a.y - origin.y
    const bx = b.x - origin.x, by = b.y - origin.y
    const dx = bx - ax, dy = by - ay
    const segLen = Math.sqrt(dx * dx + dy * dy)
    if (!Number.isFinite(segLen) || segLen < 1e-9) continue

    // Saltamos los primeros `skip` metros (pintado concatenado desde un offset).
    let localStart = 0
    if (skip > 1e-9) {
      const s = Math.min(skip, segLen)
      skip -= s
      localStart = s
      if (segLen - localStart <= 1e-9) continue // este tramo se saltó entero
    }

    const avail = segLen - localStart
    const used  = Math.min(avail, remaining)
    if (used <= 1e-9) continue

    const t0 = localStart / segLen
    const t1 = (localStart + used) / segLen
    const tMid = (t0 + t1) * 0.5
    const cx_axis = ax + dx * tMid
    const cy_axis = ay + dy * tMid

    // Vector normal al segmento.
    const nx = -dy / segLen
    const ny =  dx / segLen

    const cx = cx_axis + nx * offsetY
    const cy = cy_axis + ny * offsetY

    const geometry = new THREE.PlaneGeometry(used, laneWidth)
    const mesh = new THREE.Mesh(geometry, material)
    // Z escalonado por orden de carril para evitar z-fighting visual.
    mesh.position.set(cx, cy, 0.5 + statusOrderIndex * 0.01)
    mesh.rotation.z = Math.atan2(dy, dx)
    mesh.renderOrder = 600 + statusOrderIndex
    if (statusMeta) {
      mesh.userData.statusValue = statusMeta.status
      mesh.userData.statusLabel = statusMeta.statusLabel
      mesh.userData.statusColor = statusMeta.statusColor
      mesh.userData.statusMeters = statusMeta.meters
    }
    group.add(mesh)

    remaining -= used
  }
}
